/**
 * Время приёма, привязанного к еде, — на каждый день своё.
 *
 * Привязка к еде работала снимком: в момент нажатия бралось время приёма пищи и
 * записывалось в курс обычным числом, которое дальше повторялось каждый день.
 * Причём бралось время того дня, то есть с учётом разового переопределения, —
 * и сдвиг обеда на один день навсегда уезжал в постоянное расписание курса.
 *
 * Теперь курс хранит не время, а связь: к каким приёмам пищи он привязан. Время
 * вычисляется на каждый день из режима этого дня, поэтому сдвинутый обед двигает
 * только свой приём, а правка самого правила — все будущие.
 *
 * Функция чистая: на входе курс и приёмы пищи конкретного дня, на выходе время
 * приёма и еда, из которой оно посчитано. Ни базы, ни дат внутри — их знает
 * вызывающая сторона.
 */

import type { RoutineOccurrence } from '@/entities/routine/model/day-schedule';
import type { Medication } from '@/shared/db/schema';

import { doseTimeForMeal } from './meal-binding';
import { type ScheduleSlot, doseTimesOf } from './dose-scheduler';

/** Привязан ли курс к приёмам пищи, а не к часам. */
export function isBoundToMeals(medication: Medication): boolean {
  return (medication.mealRoutineIds ?? []).length > 0;
}

/**
 * Приёмы на конкретный день.
 *
 * `occurrences` — приёмы пищи этого дня, уже с учётом переопределений: их
 * материализует режим дня, и решать, что сегодня обед в 15:00, — его работа.
 *
 * Пустой список для привязанного курса — это не ошибка: обед бывает не каждый
 * день, а пропущенный обед не порождает приёма. Ставить «после обеда» в день
 * без обеда не от чего.
 */
export function scheduleSlotsForDate(
  medication: Medication,
  occurrences: readonly RoutineOccurrence[],
): ScheduleSlot[] {
  if (!isBoundToMeals(medication)) {
    return doseTimesOf(medication).map((scheduledMin) => ({ scheduledMin, mealRoutineId: null }));
  }

  const bound = new Set(medication.mealRoutineIds);

  // Пропущенный в этот день приём пищи до сюда не доходит вовсе: режим дня не
  // материализует его в занятия дня.
  return occurrences
    .filter((occurrence) => bound.has(occurrence.routineId))
    .map((occurrence) => ({
      mealRoutineId: occurrence.routineId,
      scheduledMin: doseTimeForMeal(
        {
          routineId: occurrence.routineId,
          title: occurrence.title,
          startMin: occurrence.startMin,
          durationMin: occurrence.durationMin,
        },
        medication.mealRelation,
        medication.mealOffsetMin,
      ),
    }))
    .sort((a, b) => a.scheduledMin - b.scheduledMin);
}
